/**
 * Fluxo de exclusão com checagem de vínculos.
 *
 * As telas de listagem que checam dependências antes de excluir (risco ocupacional, fonte
 * geradora, exame ocupacional, EPI, EPC) repetem os mesmos dois estados e as mesmas três
 * funções — só o serviço chamado e as mensagens de erro mudam. O nome do campo que indica
 * bloqueio (`hasApplications`/`hasDependencies`) e o campo com a quebra por tipo
 * (`applications`/`details`) divergem entre elas, então esse hook não tenta normalizar isso:
 * devolve `deleteDependencies` cru e cada tela lê o campo certo ao montar o diálogo.
 */
import { useState } from 'react';

interface EntityWithId {
  id: number;
}

interface UseDeleteWithDependenciesOptions<TDependencies> {
  checkDependencies: (id: number) => Promise<TDependencies>;
  deleteEntity: (id: number) => Promise<void>;
  checkErrorMessage: string;
  deleteErrorMessage: string;
  onError: (message: string) => void;
  onDeleted: () => void;
}

export function useDeleteWithDependencies<TEntity extends EntityWithId, TDependencies>({
  checkDependencies,
  deleteEntity,
  checkErrorMessage,
  deleteErrorMessage,
  onError,
  onDeleted,
}: UseDeleteWithDependenciesOptions<TDependencies>) {
  const [deleteTarget, setDeleteTarget] = useState<TEntity | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<TDependencies | null>(null);

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteDependencies(null);
  };

  const handleDeleteClick = async (entity: TEntity) => {
    setDeleteTarget(entity);
    setDeleteDependencies(null);
    try {
      const dependencies = await checkDependencies(entity.id);
      setDeleteDependencies(dependencies);
    } catch (err: unknown) {
      const error = err as { message?: string };
      onError(error.message ?? checkErrorMessage);
      closeDeleteDialog();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      await deleteEntity(deleteTarget.id);
      closeDeleteDialog();
      onDeleted();
    } catch (err: unknown) {
      const error = err as { message?: string };
      onError(error.message ?? deleteErrorMessage);
      closeDeleteDialog();
    }
  };

  return {
    deleteTarget,
    deleteDependencies,
    handleDeleteClick,
    closeDeleteDialog,
    handleDeleteConfirm,
  };
}
